'use client';

import { useState } from 'react';
import { Plus, Send, CheckCircle, AlertTriangle, Users } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDirectorInvoices, useUpdateInvoice, useDeleteInvoice } from '@/hooks/use-invoices';
import { InvoiceFormModal } from '@/components/director/invoice-form-modal';
import { BulkInvoiceWizard } from '@/components/director/bulk-invoice-wizard';
import { InvoiceTable, formatCurrency } from '@/components/director/invoices/invoice-table';
import { InvoiceFilters } from '@/components/director/invoices/invoice-filters';

export default function DirectorInvoicesPage() {
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkWizard, setShowBulkWizard] = useState(false);

  const { data, isLoading, error } = useDirectorInvoices(statusFilter);
  const updateInvoice = useUpdateInvoice();
  const deleteInvoice = useDeleteInvoice();

  const handleSendInvoice = (id: string) => {
    if (confirm('Send this invoice to the parent?')) {
      updateInvoice.mutateAsync({ id, status: 'sent' });
    }
  };

  const handleMarkPaid = (id: string) => {
    if (confirm('Mark this invoice as paid?')) {
      updateInvoice.mutateAsync({ id, status: 'paid' });
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Delete this draft invoice?')) {
      deleteInvoice.mutateAsync(id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-500">Failed to load invoices</p>
        </CardContent>
      </Card>
    );
  }

  const invoices = data?.invoices ?? [];
  const stats = data?.stats;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        subtitle={`${data?.total ?? 0} total invoices`}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowBulkWizard(true)}>
              <Users className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Generate Term Invoices</span>
            </Button>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">New Invoice</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Outstanding</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(stats.outstandingCents)}
                  </p>
                </div>
                <Send className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(stats.overdueCents)}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Paid This Month</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(stats.paidThisMonthCents)}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <InvoiceFilters
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
      />

      <InvoiceTable
        invoices={invoices}
        onSend={handleSendInvoice}
        onMarkPaid={handleMarkPaid}
        onDelete={handleDelete}
        isSending={updateInvoice.isPending}
        isDeleting={deleteInvoice.isPending}
      />

      <InvoiceFormModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
      />

      <BulkInvoiceWizard
        open={showBulkWizard}
        onOpenChange={setShowBulkWizard}
      />
    </div>
  );
}
